'use client'

import type { LucideIcon } from 'lucide-react'
import * as React from 'react'
import { useCallback, useState } from 'react'

import { cn } from '../cn'
import { Button } from './button'
import { LoadingButton } from './loading-button'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from './sheet'

type FormSheetSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full' | 'default'

interface FormSheetPrimaryAction {
  label: string
  icon?: LucideIcon
  disabled?: boolean
  loading?: boolean
  href?: string
  download?: boolean | string
  target?: React.HTMLAttributeAnchorTarget
  rel?: string
  onClick?: () => Promise<void> | void
}

interface FormSheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title: string
  description?: string
  /** Submit button label (default: "Save"). Omit onSubmit for list/picker mode — then only the cancel button is shown. */
  submitLabel?: string
  submitDisabled?: boolean
  /** Async submit handler — thrown errors are caught and displayed. Omit for list-picker sheets with no save action. */
  onSubmit?: () => Promise<void> | void
  /** Primary footer action for read-only preview sheets. Use `onSubmit` for save/create forms. */
  primaryAction?: FormSheetPrimaryAction
  /** Called when the sheet closes — use to reset form state. */
  onReset?: () => void
  /** Sheet side (default: "right") */
  side?: 'left' | 'right' | 'top' | 'bottom'
  /** Max width on desktop. Mobile is always fullscreen. Default: "md". */
  size?: FormSheetSize
  /** Footer content rendered on the left (e.g. a destructive delete button in edit mode). */
  leftFooter?: React.ReactNode
  /**
   * Header content rendered right of the title, left of the built-in X —
   * e.g. `RecordSheetFullscreenButton` for sheets with a full-page twin.
   */
  headerAction?: React.ReactNode
  /** Cancel button label (default: "Cancel"; pass "Done" for close-only pickers). */
  cancelLabel?: string
  /** Hide the cancel button entirely. */
  hideCancel?: boolean
  /** Caller-owned error string rendered above the submit button (for inline validation not thrown from onSubmit). */
  externalError?: string | null
  children: React.ReactNode
}

function FormSheet({
  open,
  onOpenChange,
  title,
  description,
  submitLabel = 'Save',
  submitDisabled,
  onSubmit,
  primaryAction,
  onReset,
  side = 'right',
  size = 'md',
  leftFooter,
  headerAction,
  cancelLabel = 'Cancel',
  hideCancel,
  externalError,
  children,
}: FormSheetProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isPrimaryActionRunning, setIsPrimaryActionRunning] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleClose = useCallback(
    (newOpen: boolean) => {
      if (!newOpen) {
        setError(null)
        onReset?.()
      }
      onOpenChange(newOpen)
    },
    [onOpenChange, onReset],
  )

  const handleSubmit = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault()
      if (!onSubmit) {
        return
      }
      setIsSubmitting(true)
      setError(null)
      try {
        await onSubmit()
        handleClose(false)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Something went wrong')
      } finally {
        setIsSubmitting(false)
      }
    },
    [onSubmit, handleClose],
  )

  const handlePrimaryActionClick = useCallback(async () => {
    if (!primaryAction?.onClick) {
      return
    }
    setIsPrimaryActionRunning(true)
    setError(null)
    try {
      await primaryAction.onClick()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong')
    } finally {
      setIsPrimaryActionRunning(false)
    }
  }, [primaryAction])

  const displayError = error ?? externalError ?? null
  const PrimaryActionIcon = primaryAction?.icon
  const showPrimaryAction = Boolean(primaryAction) && !onSubmit
  const primaryActionLoading = Boolean(primaryAction?.loading || isPrimaryActionRunning)

  const body = (
    <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-4">
      {displayError && <p className="text-sm text-destructive">{displayError}</p>}
      {children}
    </div>
  )

  const footer = (
    <div
      className={cn(
        'flex flex-col-reverse gap-2 px-6 py-4 border-t shrink-0 sm:flex-row sm:gap-0 sm:space-x-2',
        leftFooter ? 'sm:justify-between' : 'sm:justify-end',
      )}>
      {leftFooter ? <div className="flex items-center">{leftFooter}</div> : null}
      <div className="flex items-center gap-2">
        {!hideCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={() => handleClose(false)}
            disabled={isSubmitting || primaryActionLoading}
            className="cursor-pointer">
            {cancelLabel}
          </Button>
        )}
        {showPrimaryAction && primaryAction ? (
          primaryAction.href ? (
            <Button asChild className="cursor-pointer" aria-disabled={primaryAction.disabled || primaryActionLoading}>
              <a
                href={primaryAction.href}
                download={primaryAction.download}
                target={primaryAction.target}
                rel={primaryAction.rel}
                onClick={(event) => {
                  if (primaryAction.disabled || primaryActionLoading) {
                    event.preventDefault()
                  }
                }}>
                {PrimaryActionIcon ? <PrimaryActionIcon className="mr-2 h-4 w-4" /> : null}
                {primaryAction.label}
              </a>
            </Button>
          ) : (
            <LoadingButton
              type="button"
              disabled={primaryAction.disabled}
              loading={primaryActionLoading}
              icon={PrimaryActionIcon}
              onClick={() => void handlePrimaryActionClick()}
              className="cursor-pointer">
              {primaryAction.label}
            </LoadingButton>
          )
        ) : null}
        {onSubmit && (
          <LoadingButton type="submit" disabled={submitDisabled} loading={isSubmitting} className="cursor-pointer">
            {submitLabel}
          </LoadingButton>
        )}
      </div>
    </div>
  )

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent side={side} size={size} className="p-0 flex flex-col gap-0">
        <SheetHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          {headerAction ? (
            // pr-8 clears the SheetContent's built-in absolute X button.
            <div className="flex items-center gap-2 pr-8">
              <SheetTitle className="min-w-0 flex-1 truncate">{title}</SheetTitle>
              <div className="shrink-0">{headerAction}</div>
            </div>
          ) : (
            <SheetTitle>{title}</SheetTitle>
          )}
          {description && <SheetDescription>{description}</SheetDescription>}
        </SheetHeader>
        {onSubmit ? (
          <form onSubmit={handleSubmit} className="flex-1 min-h-0 flex flex-col">
            {body}
            {footer}
          </form>
        ) : (
          <>
            {body}
            {footer}
          </>
        )}
      </SheetContent>
    </Sheet>
  )
}

export { FormSheet, type FormSheetPrimaryAction, type FormSheetProps }
